/**
 * SubnationalLayer — renders admin-1 (states/provinces/CCAA) polygons on top
 * of the country base layer for countries where we have sub-national data.
 *
 *   US  → 51 states + DC
 *   ES  → 52 provinces (colored by parent autonomous community)
 *   AU  → 8 states + 2 territories
 *   CA  → 13 provinces + territories
 *   MX  → 32 states + CDMX
 *   AR  → 23 provinces + CABA
 *
 * Data source: Natural Earth Vector 1:10m admin-1, pre-extracted to
 * `/public/admin1-bundle.json` at build time — only the 6 countries above,
 * with property keys trimmed and coordinates rounded to 3 decimals.
 * Raw ≈ 3.3 MB, gzipped ≈ 900 KB. Served directly from our origin, no CDN.
 *
 * Behaviour:
 *   • Each polygon tinted by its *own* tier when we have a rule for it,
 *     otherwise by its *parent country's* tier.
 *   • Spain is special: the source file gives us provinces, but our rules
 *     are per *autonomous community*. So we use the `postal` field (which
 *     holds the CCAA code for each province) to look up the rule.
 *   • Clicking any region opens the *parent country* panel. That preserves
 *     the "easy to click at national level" UX.
 *   • Hover emits a rich payload so the map shell can render a state card
 *     next to the country card instead of just the parent country tooltip.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Geographies, Geography } from 'react-simple-maps';
import { theme } from '@/analyses/_shared/theme';
import { TIER_CONFIG, type Tier } from '@/data/tiers';
import { COUNTRY_BY_ISO2, type CountryMeta } from '@/data/countries';
import { SUBNATIONAL_RULES, type SubnationalRule } from '@/data/subnational-rules';

/** Countries we have admin-1 data for. Keep alphabetised. */
export const COUNTRIES_WITH_SUBNATIONAL = ['AR', 'AU', 'CA', 'ES', 'MX', 'US'] as const;

/** Local bundle — written by scripts/build-admin1-bundle.py / committed as static asset. */
const ADMIN1_URL = '/admin1-bundle.json';

type Admin1Props = {
  iso_a2?: string;
  iso_3166_2?: string;
  postal?: string;
  name?: string;
  name_en?: string;
  admin?: string;
  code_hasc?: string;
};

type Admin1Feature = {
  type: 'Feature';
  geometry: unknown;
  properties: Admin1Props;
  rsmKey?: string;
};

type Admin1FC = { type: 'FeatureCollection'; features: Admin1Feature[] };

/* ─────────────────────────────────────────────────────────────────────────── *
 *  Region-code normalization                                                   *
 *  Natural Earth uses ISO 3166-2 (e.g. "US-CA", "ES-CT", "AR-C", "MX-NLE")     *
 *  but our SUBNATIONAL_RULES uses its own shorthand ("CABA", "CAT", "NL", …).  *
 *  This alias map bridges them. Unmapped codes fall through to `postal` /     *
 *  the iso_3166_2 suffix.                                                     *
 * ─────────────────────────────────────────────────────────────────────────── */
const RULE_CODE_ALIASES: Record<string, string> = {
  // Argentina — rule uses "CABA" / "PBA"; ISO uses single-letter codes
  'AR-C': 'CABA',  // Ciudad Autónoma de Buenos Aires
  'AR-B': 'PBA',   // Provincia de Buenos Aires
  // Mexico — rule uses "NL" / "JAL"; ISO uses 3-letter codes
  'MX-NLE': 'NL',
  'MX-JAL': 'JAL',
};

/** Per-country overrides when the feature's iso_3166_2 isn't the right grain.
 *  For Spain we want the *autonomous community* rule, not the province rule,
 *  so we key off `postal` which NE sets to the CCAA code. */
function countrySpecificRegionKey(
  iso: string,
  props: Admin1Props,
): string | null {
  if (iso === 'ES') {
    const postal = (props.postal ?? '').toUpperCase();
    // Natural Earth's postal for ES provinces = CCAA code (AN, CT, MD, PV, GA, …).
    // Our rules use "CAT" for Catalonia, "MAD" for Madrid.
    const POSTAL_TO_RULE: Record<string, string> = {
      AN: 'AN', CT: 'CAT', GA: 'GA', MD: 'MAD', PV: 'PV',
    };
    return POSTAL_TO_RULE[postal] ?? postal ?? null;
  }
  return null;
}

export type HoveredSubRegion = {
  /** Parent country meta, for the secondary line */
  country: CountryMeta;
  /** Display name of the region (e.g. "California", "Cádiz", "Jalisco") */
  regionName: string;
  /** Rule-system short code (CA, AN, JAL, …) */
  regionCode: string;
  /** The CCAA / state rule, if we have one recorded */
  rule: SubnationalRule | null;
  /** Effective tier = rule tier or parent country tier */
  tier: Tier;
};

export type SubnationalStatus = {
  loading: boolean;
  error: string | null;
  ready: boolean;
};

export type SubnationalLayerProps = {
  /** Toggle rendering. Once fetched we keep the data in state; re-enabling is instant. */
  enabled: boolean;
  onCountryClick: (countryName: string) => void;
  /** Rich hover payload — emits `null` on leave. */
  onHover: (h: HoveredSubRegion | null) => void;
  /** Optional country-level hover — keeps the country card alive. */
  onCountryHover?: (countryName: string | null) => void;
  /** Loading / error state surfaced to the map shell. */
  onStatus?: (s: SubnationalStatus) => void;
  /** ISO2 of the hovered country (for highlighting). */
  highlightedIso2?: string | null;
};

/** Given a feature's properties, resolve which SUBNATIONAL_RULES entry (if any) applies. */
function resolveRule(
  props: Admin1Props,
  ruleIndex: Map<string, SubnationalRule>,
): { countryIso: string; regionKey: string; rule: SubnationalRule | null } | null {
  const countryIso = (props.iso_a2 ?? '').toUpperCase();
  if (!countryIso || countryIso === '-99') return null;

  const candidates: string[] = [];

  const countrySpecific = countrySpecificRegionKey(countryIso, props);
  if (countrySpecific) candidates.push(countrySpecific);

  const iso2 = (props.iso_3166_2 ?? '').toUpperCase();
  if (iso2 && RULE_CODE_ALIASES[iso2]) {
    candidates.push(RULE_CODE_ALIASES[iso2]);
  }
  if (iso2.includes('-')) {
    candidates.push(iso2.split('-').pop() ?? '');
  }

  const postal = (props.postal ?? '').toUpperCase();
  if (postal) candidates.push(postal);

  // Try each candidate against the rules index — first hit wins.
  for (const c of candidates) {
    if (!c) continue;
    const rule = ruleIndex.get(`${countryIso}:${c.toUpperCase()}`);
    if (rule) {
      return { countryIso, regionKey: c.toUpperCase(), rule };
    }
  }

  // No rule — still return the first viable key so we can show the parent-country tier.
  const fallback = candidates.find(Boolean) ?? '';
  return { countryIso, regionKey: fallback.toUpperCase(), rule: null };
}

function indexRules(): Map<string, SubnationalRule> {
  const m = new Map<string, SubnationalRule>();
  for (const r of SUBNATIONAL_RULES) {
    m.set(`${r.country}:${r.region.toUpperCase()}`, r);
  }
  return m;
}

export const SubnationalLayer = ({
  enabled,
  onCountryClick,
  onHover,
  onCountryHover,
  onStatus,
  highlightedIso2,
}: SubnationalLayerProps) => {
  const [data, setData] = useState<Admin1FC | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fetchStartedRef = useRef(false);

  /* Lazy-fetch on first enable. Only depend on `enabled` — state changes during
   * the fetch would otherwise re-run this effect and fire its cleanup, which
   * would cancel the in-flight response. */
  useEffect(() => {
    if (!enabled || fetchStartedRef.current) return;
    fetchStartedRef.current = true;
    setLoading(true);
    fetch(ADMIN1_URL)
      .then((r) => {
        if (!r.ok) throw new Error(`Admin-1 fetch failed: ${r.status}`);
        return r.json() as Promise<Admin1FC>;
      })
      .then((fc) => setData(fc))
      .catch((e: Error) => {
        fetchStartedRef.current = false; // re-arm so toggle-off-then-on retries
        setError(e.message);
        // eslint-disable-next-line no-console
        console.error('SubnationalLayer: admin-1 fetch failed', e);
      })
      .finally(() => setLoading(false));
  }, [enabled]);

  const ruleIndex = useMemo(indexRules, []);

  useEffect(() => {
    onStatus?.({ loading, error, ready: data !== null });
  }, [loading, error, data, onStatus]);

  if (!enabled || !data) return null;

  return (
    <Geographies geography={data}>
      {({ geographies }) =>
        geographies.map((geo) => {
          const props = (geo.properties ?? {}) as Admin1Props;
          const resolved = resolveRule(props, ruleIndex);
          if (!resolved) return null;

          const parent = COUNTRY_BY_ISO2[resolved.countryIso];
          if (!parent) return null;

          // Tier priority: region's own rule tier > parent country tier
          const tier: Tier = resolved.rule?.tier ?? parent.tier ?? 'not-analyzed';
          const fill = TIER_CONFIG[tier].color;

          const inHoveredCountry =
            highlightedIso2 && resolved.countryIso === highlightedIso2;

          const regionName = props.name_en ?? props.name ?? resolved.regionKey;

          return (
            <Geography
              key={geo.rsmKey}
              geography={geo}
              onClick={() => onCountryClick(parent.name)}
              onMouseEnter={() => {
                onHover({
                  country: parent,
                  regionName,
                  regionCode: resolved.regionKey,
                  rule: resolved.rule,
                  tier,
                });
                onCountryHover?.(parent.name);
              }}
              onMouseLeave={() => {
                onHover(null);
                onCountryHover?.(null);
              }}
              style={{
                default: {
                  fill,
                  fillOpacity: inHoveredCountry ? 0.88 : 0.72,
                  stroke: theme.DARK2,
                  strokeWidth: 0.3,
                  strokeOpacity: 0.9,
                  outline: 'none',
                  transition: 'fill-opacity 0.15s, stroke-width 0.15s',
                },
                hover: {
                  fill,
                  fillOpacity: 0.95,
                  stroke: theme.WHITE,
                  strokeWidth: 1,
                  outline: 'none',
                  cursor: 'pointer',
                },
                pressed: {
                  fill,
                  fillOpacity: 1,
                  outline: 'none',
                },
              }}
            />
          );
        })
      }
    </Geographies>
  );
};
